import styled, { keyframes } from "styled-components"
import { Container, Row, Col } from "react-bootstrap"
import appendCDN from "~/utils/append_cdn_link"
import Box from "~/components/UI/Box"
import Text from "~/components/UI/Text"
import { useTranslation } from "~/i18n"
import SharedTitle from "~/components/UI/SharedTitle"
import { HEADER_HEIGHT } from "~/components/UI/Constants"
import { useEffect, useMemo, useState } from "react"
import gsap from '~/modules/gsap'
import Link from "~/components/UI/Link"
import Button from "~/components/Button"
import Router from 'next/router'
import breakpoints from "~/components/UI/breakpoints"
import getcdn from "~/modules/get_cdn_link"


interface Press {
  logo: string // e.g /img/dantri.png, dont use full url, I will prepend cdn link
  date: number | string
  title: string
  summary: string,
  press_name: string
  link: string
}

const MyContainer = styled(Container)`
  padding: ${HEADER_HEIGHT + 32}px 0;
  padding-bottom: 50px;
  ${breakpoints.md} {
    padding-bottom: auto;
  }
`
const Background = styled.div`
  position: absolute;
  background-image: url(${getcdn('/img/news-bg.jpg?ver=2')});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  height: 670px;
  width: 100%;
  left: 0;
  top: 0;
  .news-bg-cover {
    width: 100%;
    height: 100%;
    opacity: 0;
    background-color: black;
  }
  ${breakpoints.md} {
    height: 600px;
  }
`

const Quote = styled(Text)`
  max-width: 720px;
  color: #fff;
  margin: auto;
  text-align: center;
  position: relative;
  opacity: 0.7;
  ::before, ::after {
    content: '';
    background-image: url(${appendCDN('/img/quotes.svg')});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    position: absolute;
    height: 48px;
    width: 48px;
  }

  ::before {
    transform: rotate(180deg);
    left: 0;
    top: -50px
  }

  ::after {
    right: 0px;
    bottom: 50px;
  }

  ${breakpoints.md} {

    ::before, ::after {
      height: 80px;
      width: 80px;
    }

    ::before {
      left: -90px;
      top: -40px
    }

    ::after {
      right: -90px;
      bottom: 40px;
    }
  }
`

const loop = keyframes`
  0 {
    transform: translateX(0)
  }

  100% {
    transform: translateX(-100%)
  }
`

const Img = styled.img`
  padding-right: 16px;
  height: 100px;
  animation: ${loop} infinite 20s linear;

`

const PressListContainer = styled(Box)`
  padding: 0;
  :hover {
    .press-list {
      animation-play-state: paused;
    }
  }
`

const NewsContainer = styled(Container)`
  border-bottom: 1px solid rgba(26, 26, 26, 0.1);
  padding: 32px 0;
`

const Logo = styled.img`
  width: 70%;
`

const Pagination = styled(Box)`
  button {
    margin: 0 8px;
    height: 40px;
    min-width: 40px;

    :first-child, :last-child {
      width: 70px;
    }
  }

  .page-index {
    display: none;
    ${breakpoints.md} {
      display: flex;
    }
  }
`

const ListContainer = styled(Box)`
  >div:last-child {
    border-bottom: 0
  }
  margin-bottom: 20px;
  ${breakpoints.md} {
    margin-bottom: 80px;
  }
`

const LogoContainer = styled(Box)`
  text-align: center;
  ${breakpoints.md} {
    text-align: left;
  }
`

const URL = styled(Text)`
  transition: 500ms;
  :hover {
    color: #0084ff;
  }
`

const PressItem: React.FC<Press> = ({
  logo,
  title,
  date,
  summary,
  link,
}) => {

  const domain = useMemo(() => {
    const value = link.replace(/(https?\:\/\/)/gi, '').split('/')
    return value?.[0];
  }, [link])

  return (
    <NewsContainer>
      <Row>
        <Col md={4} className='mb-5'>
          <LogoContainer>
            <Logo src={appendCDN(`img/${logo}`)} />
          </LogoContainer>
        </Col>
        <Col md={8}>
          <Text>
            <strong>{domain}</strong> | {date}
          </Text>

          <Link target='_blank' href={link}>
            <URL padding='8px 0 16px 0' size={24} bold>
              {title}
            </URL>
          </Link>
          <Text>
            {summary}
          </Text>
        </Col>
      </Row>
    </NewsContainer>
  )
}

const Press: React.FC<{ press: Press[], defaultPage?: number, webview?: any }> = ({ press, defaultPage = 0, webview = undefined }) => {
  const [t] = useTranslation()
  const [page, setPage] = useState(isNaN(defaultPage) ? 0 : defaultPage)
  const limit = 10;
  const total = useMemo(() => {
    if (Array.isArray(press)) {
      return Math.ceil(press.length / limit)
    }
    return 0
  }, [press])

  useEffect(() => {
    webview ? Router.replace(Router.pathname + `?page=${page + 1}&webview=${webview}`)
      : Router.replace(Router.pathname + `?page=${page + 1}`)

  }, [page])

  const pagination = useMemo(() => {
    const pages: JSX.Element[] = []
    for (let i = 0; i < total; i++) {
      pages.push(
        <Button className='page-index' onClick={() => {
          setPage(i)
          gsap.to(window, { duration: 0.25, scrollTo: 560 })
        }} key={i} border={i === page ? '1px solid' : '0'} borderColor={i === page ? 'rgba(26, 26, 26, 0.1)' : ''}>
          <Text>
            {i + 1}
          </Text>
        </Button>
      )
    }
    return pages
  }, [total, page])

  const pressList = useMemo(() => {
    if (Array.isArray(press)) {
      const list = [...press].splice(page * limit, limit)
      return list.map(item => <PressItem key={item.press_name} {...item} />)
    }

    return []
  }, [press, page])

  const previousButton = useMemo(() => {
    if (total > 2) {
      const onClick = () => {
        gsap.to(window, { duration: 0.25, scrollTo: 620 })
        setPage(prev => {
          if (prev > 0) {
            return prev - 1
          }
          return prev
        })
      }
      return (
        <Button onClick={onClick} borderColor='rgba(26, 26, 26, 0.1)'>
          <Text>
            {t`Trước`}
          </Text>
        </Button>
      )
    }
  }, [total])

  const nextButton = useMemo(() => {
    if (total > 2) {
      const onClick = () => {
        gsap.to(window, { duration: 0.25, scrollTo: 620 })
        setPage(prev => {
          if (prev < total - 1) {
            return prev + 1
          }
          return prev
        })
      }
      return (
        <Button onClick={onClick} borderColor='rgba(26, 26, 26, 0.1)'>
          <Text>
            {t`Sau`}
          </Text>
        </Button>
      )
    }
  }, [total])

  return (
    <>
      <Background id='news-bg'>
        <div className='news-bg-cover' />
      </Background>
      <MyContainer fluid>
        <Container>
          <Box style={{ height: 560 }}>
            <SharedTitle padding='0 0 72px 0' color='#fff' centered>
              {t`Báo chí nói về Infina`}
            </SharedTitle>
            <Quote padding='0 0 60px 0'>
              {t`REALSTAKE_BRING_VALUE`}
              <Text centered color='#fff' padding='16px 0 0 0'>
                - Theo CafeBiz -
              </Text>
            </Quote>
            <PressListContainer height='auto' flex style={{ overflow: 'hidden' }}>
              <Img className='press-list' src={appendCDN('/img/partners.png')} />
              <Img className='press-list' src={appendCDN('/img/partners.png')} />
            </PressListContainer>
          </Box>
        </Container>

        <Box id='press-content' style={{ background: '#fff' }}>
          <Container fluid>
            <ListContainer>
              {pressList}
            </ListContainer>
            <Box>
              <Pagination flex justify='center'>
                {previousButton}
                {pagination}
                {nextButton}
              </Pagination>
            </Box>
          </Container>
        </Box>
      </MyContainer>
    </>
  )
}

export default Press
